import React, { useMemo } from "react";
import { Chip, Typography } from "@mui/material";
import {
  REFERRAL_PIPELINE_STATUSES,
  pipelineStatusColors,
  pipelineStatusLabels,
} from "../../../common/ReferralStatusChip";
import { EcosystemPartner, completionRateOf } from "./ecosystemApi";
import GroupTable, { GroupColumn } from "./GroupTable";
import ExportBar, { buildGrid } from "./ExportBar";
import {
  CompletionBar,
  EmptyState,
  SectionCard,
  SectionTitle,
  percentText,
} from "./parts";

export type PipelineGroupBy = "partner" | "category";

/** One table row — a partner or a category rollup of partners. */
export interface PipelineRow {
  id: string;
  name: string;
  category: string;
  kind?: "provider" | "resource";
  partnerCount: number;
  byStatus: { [status: string]: number };
  total: number;
  clients: number;
  completionRate: number;
}

const kindLabel = (kind?: string) =>
  kind === "provider" ? "Provider" : kind === "resource" ? "Resource" : "";

export const toPartnerRows = (partners: EcosystemPartner[]): PipelineRow[] =>
  partners.map((partner) => ({
    id: `${partner.kind}:${partner.id}`,
    name: partner.name,
    category: partner.category,
    kind: partner.kind,
    partnerCount: 1,
    byStatus: partner.byStatus,
    total: partner.total,
    clients: partner.distinctClients,
    completionRate: partner.completionRate,
  }));

export const toCategoryRows = (partners: EcosystemPartner[]): PipelineRow[] => {
  const byCategory = new Map<string, PipelineRow>();
  partners.forEach((partner) => {
    const key = partner.category || "Uncategorized";
    let row = byCategory.get(key);
    if (!row) {
      row = {
        id: key,
        name: key,
        category: key,
        partnerCount: 0,
        byStatus: {},
        total: 0,
        clients: 0,
        completionRate: 0,
      };
      byCategory.set(key, row);
    }
    row.partnerCount += 1;
    row.total += partner.total;
    row.clients += partner.distinctClients;
    REFERRAL_PIPELINE_STATUSES.forEach((status) => {
      row!.byStatus[status] =
        (row!.byStatus[status] || 0) + (partner.byStatus[status] || 0);
    });
  });
  const rows = Array.from(byCategory.values());
  rows.forEach((row) => {
    row.completionRate = completionRateOf(row.byStatus);
  });
  return rows;
};

const statusColumns: GroupColumn<PipelineRow>[] = REFERRAL_PIPELINE_STATUSES.map(
  (status) => ({
    key: `status:${status}`,
    label: pipelineStatusLabels[status] || status,
    numeric: true,
    headerColor: pipelineStatusColors[status],
    sortValue: (row: PipelineRow) => row.byStatus[status] || 0,
    render: (row: PipelineRow) => (
      <span
        style={{
          color: row.byStatus[status]
            ? pipelineStatusColors[status]
            : "#c5c5cc",
          fontWeight: row.byStatus[status] ? 700 : 400,
        }}
      >
        {row.byStatus[status] || 0}
      </span>
    ),
  }),
);

const tailColumns = (clientsLabel: string): GroupColumn<PipelineRow>[] => [
  {
    key: "total",
    label: "Total",
    numeric: true,
    sortValue: (row) => row.total,
    render: (row) => <strong>{row.total.toLocaleString()}</strong>,
  },
  {
    key: "clients",
    label: clientsLabel,
    numeric: true,
    sortValue: (row) => row.clients,
  },
  {
    key: "completionRate",
    label: "Completion Rate",
    numeric: true,
    width: 170,
    sortValue: (row) => row.completionRate,
    render: (row) => <CompletionBar rate={row.completionRate} />,
    csvValue: (row) => percentText(row.completionRate),
  },
];

export const pipelineColumns = (
  groupBy: PipelineGroupBy,
): GroupColumn<PipelineRow>[] =>
  groupBy === "partner"
    ? [
        {
          key: "name",
          label: "Partner",
          sortValue: (row) => row.name,
          render: (row) => (
            <Typography sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
              {row.name}
            </Typography>
          ),
        },
        {
          key: "category",
          label: "Category",
          sortValue: (row) => row.category,
        },
        {
          key: "kind",
          label: "Kind",
          sortValue: (row) => kindLabel(row.kind),
          render: (row) => (
            <Chip
              label={kindLabel(row.kind)}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: "0.65rem" }}
            />
          ),
          csvValue: (row) => kindLabel(row.kind),
        },
        ...statusColumns,
        ...tailColumns("Clients"),
      ]
    : [
        {
          key: "name",
          label: "Category",
          sortValue: (row) => row.name,
          render: (row) => (
            <Typography sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
              {row.name}
            </Typography>
          ),
        },
        {
          key: "partnerCount",
          label: "Partners",
          numeric: true,
          sortValue: (row) => row.partnerCount,
        },
        ...statusColumns,
        ...tailColumns("Clients*"),
      ];

interface PipelineGroupingProps {
  partners: EcosystemPartner[];
  groupBy: PipelineGroupBy;
  filenamePrefix: string;
}

/** Partner and category groupings — same funnel columns, different rollup. */
const PipelineGrouping: React.FC<PipelineGroupingProps> = ({
  partners,
  groupBy,
  filenamePrefix,
}) => {
  const rows = useMemo(
    () =>
      groupBy === "partner" ? toPartnerRows(partners) : toCategoryRows(partners),
    [partners, groupBy],
  );
  const columns = useMemo(() => pipelineColumns(groupBy), [groupBy]);

  if (rows.length === 0) {
    return (
      <EmptyState
        title="No referrals found for this period"
        hint="Try widening the date range or switching the partner-kind filter."
      />
    );
  }

  return (
    <SectionCard>
      <SectionTitle
        subtitle={
          groupBy === "partner"
            ? "Every partner with its full pipeline funnel"
            : "Partners rolled up by category — *client counts are summed per partner, so a client referred to two partners in a category counts twice"
        }
      >
        {groupBy === "partner" ? "By Partner" : "By Category"}
      </SectionTitle>
      <GroupTable
        columns={columns}
        rows={rows}
        getRowKey={(row) => row.id}
        defaultSortKey="total"
      />
      <ExportBar
        getGrid={() => buildGrid(columns, rows)}
        filename={`${filenamePrefix}_${groupBy}.csv`}
      />
    </SectionCard>
  );
};

export default PipelineGrouping;
